// Mirrors backend services/semver.go: MAJOR.MINOR.PATCH with an optional
// pre-release, no build metadata, and a single optional leading "v".
const SEMVER_PATTERN =
  /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-((?:0|[1-9]\d*|\d*[A-Za-z-][0-9A-Za-z-]*)(?:\.(?:0|[1-9]\d*|\d*[A-Za-z-][0-9A-Za-z-]*))*))?$/;

export const SEMVER_HINT =
  "Use a semantic version such as 1.0.0 (an optional pre-release like 1.0.0-rc.1 is allowed).";

export function normalizeSemver(raw: string): string | null {
  let v = raw.trim();
  if (v.startsWith("v") || v.startsWith("V")) v = v.slice(1);
  if (v.length > 64 || !SEMVER_PATTERN.test(v)) return null;
  return v;
}

interface ParsedSemver {
  core: [number, number, number];
  pre: string[];
}

function parseSemver(v: string): ParsedSemver | null {
  const m = SEMVER_PATTERN.exec(v);
  if (!m) return null;
  return {
    core: [Number(m[1]), Number(m[2]), Number(m[3])],
    pre: m[4] ? m[4].split(".") : [],
  };
}

function comparePrerelease(a: string[], b: string[]): number {
  if (a.length === 0 && b.length === 0) return 0;
  if (a.length === 0) return 1;
  if (b.length === 0) return -1;
  for (let i = 0; i < Math.min(a.length, b.length); i++) {
    const x = a[i]!;
    const y = b[i]!;
    const xNum = /^\d+$/.test(x);
    const yNum = /^\d+$/.test(y);
    if (xNum && yNum) {
      const diff = Number(x) - Number(y);
      if (diff !== 0) return diff < 0 ? -1 : 1;
    } else if (xNum !== yNum) {
      return xNum ? -1 : 1;
    } else if (x !== y) {
      return x < y ? -1 : 1;
    }
  }
  if (a.length === b.length) return 0;
  return a.length < b.length ? -1 : 1;
}

// compareSemver orders by semantic-version precedence (-1, 0, 1).
export function compareSemver(a: string, b: string): number {
  const pa = parseSemver(a);
  const pb = parseSemver(b);
  if (!pa && !pb) return a < b ? -1 : a > b ? 1 : 0;
  if (!pa) return -1;
  if (!pb) return 1;
  for (let i = 0; i < 3; i++) {
    if (pa.core[i] !== pb.core[i]) return pa.core[i]! < pb.core[i]! ? -1 : 1;
  }
  return comparePrerelease(pa.pre, pb.pre);
}

// suggestNextVersions proposes patch/minor/major increments of the base
// version that are not already taken. Suggestions are only conveniences.
export function suggestNextVersions(
  base: string | undefined,
  taken: string[] = [],
): string[] {
  const parsed = base ? parseSemver(base) : null;
  const [major, minor, patch] = parsed?.core ?? [1, 0, 0];
  const candidates = parsed
    ? parsed.pre.length > 0
      ? [`${major}.${minor}.${patch}`]
      : [
          `${major}.${minor}.${patch + 1}`,
          `${major}.${minor + 1}.0`,
          `${major + 1}.0.0`,
        ]
    : ["1.0.0"];
  const used = new Set(taken);
  return candidates.filter((c) => !used.has(c));
}
